import { useRagnar } from '@mentimeter/ragnar-react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { ImageProgressive } from '@mentimeter/ragnar-ui/image-progressive';
import { isEmptyObject } from '@tiptap/core';
import { useTranslations } from 'next-intl';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import { StaggerCss } from '../../shared/components/StaggerCss';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { PlaceholderImage } from './PlaceholderImage';

interface ImagePresentationProps {
  imageUrl: string | undefined;
  imageAltDescription: string | undefined;
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  isFullSizeImage: boolean;
  shouldAnimate: boolean;
  CanvasTitleArea: InteractiveCanvasComponents['CanvasTitleArea'];
}

export function ImagePresentation({
  imageUrl,
  imageAltDescription,
  title,
  styledTitle,
  label,
  isFullSizeImage,
  shouldAnimate,
  CanvasTitleArea,
}: ImagePresentationProps) {
  const t = useTranslations('presenting');
  const {
    theme: {
      visualizationColors: { titleColor },
    },
  } = useRagnar();

  // When we are in Editor always show TitleSection. If we are in a presentation we only show if there is a styled/non-styled title. Otherwise hide since we don't want the container taking up empty space.
  const showTitleSection = Boolean(
    CanvasTitleArea || !isEmptyObject(styledTitle) || title,
  );

  return (
    <Box
      width="100%"
      flex={1}
      data-testid="image-container"
      alignItems="center"
      justifyContent="center"
    >
      <StaggerCss index={1} type="fade" enableAnimations={shouldAnimate}>
        <Box
          flex="1 1 auto"
          width="100%"
          alignItems="center"
          justifyContent="center"
          mb={title || label ? 3 : 0}
        >
          <Box height="100%" width="100%" position="absolute" top={0} left={0}>
            {!isFullSizeImage &&
              (imageUrl ? (
                <ImageProgressive
                  src={imageUrl}
                  alt={imageAltDescription || title}
                  stretch
                />
              ) : (
                <PlaceholderImage />
              ))}
          </Box>
        </Box>
      </StaggerCss>
      <Box width="100%" alignItems="center" py="space1" pl="space2">
        {showTitleSection ? (
          <TitleSection
            enablePlaceholderAlignment
            hideAIEditingSuggestions
            alignItems="center"
            textAlign="center"
            title={title}
            styledTitle={styledTitle}
            label={label}
            fontSizeOffset={0}
            mb="space0"
            showTextSizeDropdown={false}
            color={titleColor}
            placeholder={t('image_slide_caption_placeholder')}
          />
        ) : null}
      </Box>
    </Box>
  );
}
